/**
 * CRM(zep-crm) HTTP 클라이언트.
 *
 * 브라우저가 `${NEXT_PUBLIC_CRM_URL}` 를 직접 부른다. CRM CORS 가 quiz 오리진
 * (운영·dev-N·프리뷰)만 허용하고, 레이트리밋(IP 3/분)도 프록시 IP 가 아니라 실제
 * 사용자 IP 로 걸려야 하기 때문이다.
 *
 * 호출부는 이 인터페이스에 의존한다 — 스토리·테스트는 `window.fetch` 를 갈아끼우지
 * 말고 가짜 클라이언트를 주입한다.
 */

export interface CrmErrorBody {
  error?: {
    code?: string;
    message?: string;
    /** 서버가 지목한 입력 필드 → 메시지 */
    fields?: Record<string, string | undefined>;
    raw?: unknown;
  };
}

export class CrmApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: CrmErrorBody,
    fallbackMessage: string,
  ) {
    super(body.error?.message || fallbackMessage);
    this.name = 'CrmApiError';
  }
}

export interface CrmClient {
  /** 성공 시 파싱된 응답 본문. 실패면 CrmApiError 를 throw 한다. */
  post(path: string, body: unknown): Promise<unknown>;
  get(path: string): Promise<unknown>;
}

interface CrmClientOptions {
  /** 사용자에게 보일 문구는 도메인마다 달라 호출부가 정한다. */
  messages: {
    /** NEXT_PUBLIC_CRM_URL 이 없을 때 */
    notConfigured: string;
    /** 그 밖의 실패(서버가 메시지를 안 줬을 때의 기본값) */
    failed: string;
  };
  /** 주로 테스트용. 기본은 호출 시점의 `process.env.NEXT_PUBLIC_CRM_URL`. */
  resolveBaseUrl?: () => string | undefined;
  fetchImpl?: typeof fetch;
}

/**
 * 호출 주소를 정한다. 로컬(LOC)은 next.config 의 `/crm/*` → dev-crm 프록시를 타고,
 * 그 외는 NEXT_PUBLIC_CRM_URL 절대 주소를 쓴다. 로컬에 그 env 가 없어서 견적 제출을
 * 아예 못 눌러보는 상태였다.
 */
function resolveUrl(
  path: string,
  rawBase: string | undefined,
  notConfiguredMessage: string,
): string {
  if (process.env.NEXT_PUBLIC_STAGE === 'LOC') {
    return `/crm${path}`;
  }

  const base = rawBase?.trim();
  // 주소가 없으면 부르지 않는다 — 다른 환경으로 조용히 새지 않게.
  if (!base) {
    throw new CrmApiError(
      0,
      { error: { code: 'crm_not_configured' } },
      notConfiguredMessage,
    );
  }
  // base 에 경로(/api)가 붙은 환경도 오리진 기준으로 맞춘다.
  return new URL(path, base).toString();
}

async function parseBody(response: Response, fallbackMessage: string) {
  const text = await response.text();
  if (!text) return {};

  try {
    return JSON.parse(text) as unknown;
  } catch (_error) {
    return {
      error: {
        code: 'invalid_json_response',
        message: fallbackMessage,
        raw: text,
      },
    } satisfies CrmErrorBody;
  }
}

export function createCrmClient({
  messages,
  resolveBaseUrl = () => process.env.NEXT_PUBLIC_CRM_URL,
  fetchImpl,
}: CrmClientOptions): CrmClient {
  async function request(path: string, init: RequestInit): Promise<unknown> {
    const url = resolveUrl(path, resolveBaseUrl(), messages.notConfigured);
    const doFetch = fetchImpl ?? fetch;
    const response = await doFetch(url, init);

    const parsed = await parseBody(response, messages.failed);
    if (!response.ok) {
      throw new CrmApiError(
        response.status,
        parsed as CrmErrorBody,
        messages.failed,
      );
    }

    return parsed;
  }

  return {
    post(path, body) {
      return request(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    },
    get(path) {
      return request(path, { method: 'GET' });
    },
  };
}
